import { v7 as uuidv7 } from 'uuid'
import { z } from 'zod'
import { LOCAL_DATE_RE, localDateOf } from './dates.js'
import {
  AGE_BANDS,
  CLOSING_WINDOWS,
  CONTENT_ID_RE,
  DECOR_SLOTS,
  GAME_IDS,
  LESSON_IDS,
  MODES,
  OUTCOMES,
  RECAP_QUESTION_ID_RE,
  SLOTS,
  WINDOWS,
} from './ids.js'

/**
 * Every event type in ARCHITECTURE §4.3 with a strict payload. CONTRACTS §2 rule 5: adding a
 * field to `brush_session` needs a COMPLIANCE §2 review note. Strictness is what stops a
 * client smuggling extra fields through sync (CONTRACTS §8).
 */

const localDate = z.string().regex(LOCAL_DATE_RE, 'YYYY-MM-DD')
const month = z.string().regex(/^\d{4}-\d{2}$/, 'YYYY-MM')
const contentId = z.string().regex(CONTENT_ID_RE, 'content id')
const nickname = z.string().min(1).max(16)
const recapQuestionId = z.string().regex(RECAP_QUESTION_ID_RE, 'recap question id')
const attempt = z.union([z.literal(1), z.literal(2), z.literal(3)])
/** A question's attempt number (CONTRACTS §6): the third try shows the answer. */
export type Attempt = z.infer<typeof attempt>
const petName = z.string().min(1).max(12)

export const LocalDateSchema = localDate

const envelope = {
  id: z.uuid(),
  childId: z.uuid(),
  deviceId: z.uuid(),
  ts: z.number().int().nonnegative(),
  localDate,
  seq: z.number().int().positive().optional(),
  reviewMode: z.literal(true).optional(),
  v: z.literal(1),
}

/** Profile fields the parent can set. `profile_updated` carries any subset of them. */
const profileFields = {
  nickname,
  ageBand: z.enum(AGE_BANDS),
  avatarId: contentId,
  petName,
}

const payloads = {
  profile_created: z.strictObject(profileFields),
  profile_updated: z.strictObject(profileFields).partial(),
  app_opened: z.strictObject({}),
  brush_session: z.strictObject({
    window: z.enum(WINDOWS),
    outcome: z.enum(OUTCOMES),
    activeSeconds: z.number().min(0).max(3600),
    facePresentSeconds: z.number().min(0).max(3600),
    sessionSeconds: z.number().min(0).max(3600),
    cameraUsed: z.boolean(),
    mode: z.enum(MODES),
  }),
  lesson_started: z.strictObject({ lessonId: z.enum(LESSON_IDS) }),
  lesson_completed: z.strictObject({ lessonId: z.enum(LESSON_IDS) }),
  quiz_answered: z.strictObject({
    lessonId: z.enum(LESSON_IDS),
    attempt,
    optionId: z.string().min(1).max(8),
    correct: z.boolean(),
  }),
  chest_opened: z.strictObject({ lessonId: z.enum(LESSON_IDS) }),
  minigame_played: z.strictObject({
    gameId: z.enum(GAME_IDS),
    score: z.number().int().min(0).max(100_000),
    tutorial: z.boolean(),
  }),
  pet_fed: z.strictObject({ foodId: contentId }),
  item_purchased: z.strictObject({ itemId: contentId }),
  item_equipped: z.strictObject({
    slot: z.enum(SLOTS),
    itemId: contentId.nullable(),
  }),
  decor_placed: z.strictObject({
    slot: z.enum(DECOR_SLOTS),
    itemId: contentId.nullable(),
  }),
  pet_renamed: z.strictObject({ petName }),
  mode_changed: z.strictObject({ mode: z.enum(MODES) }),
  review_mode_set: z.strictObject({ on: z.boolean() }),
  parent_observation: z.strictObject({
    month,
    thumb: z.boolean(),
    mouthBreath: z.boolean(),
    tongueThrust: z.boolean(),
  }),
  window_closed: z.strictObject({
    window: z.enum(CLOSING_WINDOWS),
    localDate,
  }),
  streak_freeze_used: z.strictObject({ localDate }),
  /** W3-OVERHAUL §3.1: the Hatırlatma Eğitimi and the Günlük Görevler. */
  recap_started: z.strictObject({}),
  recap_answered: z.strictObject({
    questionId: recapQuestionId,
    attempt,
    optionId: z.string().min(1).max(8),
    correct: z.boolean(),
  }),
  recap_completed: z.strictObject({ localDate }),
  gorev_answered: z.strictObject({
    localDate,
    challengeId: recapQuestionId,
    attempt,
    optionId: z.string().min(1).max(8),
    correct: z.boolean(),
  }),
} as const

export type EventType = keyof typeof payloads

function memberOf<T extends EventType>(type: T) {
  return z.strictObject({ ...envelope, type: z.literal(type), payload: payloads[type] })
}

export const EventSchema = z.discriminatedUnion('type', [
  memberOf('profile_created'),
  memberOf('profile_updated'),
  memberOf('app_opened'),
  memberOf('brush_session'),
  memberOf('lesson_started'),
  memberOf('lesson_completed'),
  memberOf('quiz_answered'),
  memberOf('chest_opened'),
  memberOf('minigame_played'),
  memberOf('pet_fed'),
  memberOf('item_purchased'),
  memberOf('item_equipped'),
  memberOf('decor_placed'),
  memberOf('pet_renamed'),
  memberOf('mode_changed'),
  memberOf('review_mode_set'),
  memberOf('parent_observation'),
  memberOf('window_closed'),
  memberOf('streak_freeze_used'),
  memberOf('recap_started'),
  memberOf('recap_answered'),
  memberOf('recap_completed'),
  memberOf('gorev_answered'),
])

export const EVENT_TYPES = Object.keys(payloads) as EventType[]

export type AnyEvent = z.infer<typeof EventSchema>

export type EventOf<T extends EventType> = Extract<AnyEvent, { type: T }>
export type PayloadOf<T extends EventType> = z.infer<(typeof payloads)[T]>

export function payloadSchema<T extends EventType>(type: T): (typeof payloads)[T] {
  return payloads[type]
}

export function isEventType(value: string): value is EventType {
  return Object.hasOwn(payloads, value)
}

export function parseEvent(value: unknown): AnyEvent {
  return EventSchema.parse(value)
}

export function safeParseEvent(
  value: unknown,
): { ok: true; event: AnyEvent } | { ok: false; issue: string } {
  const result = EventSchema.safeParse(value)
  if (result.success) return { ok: true, event: result.data }
  const first = result.error.issues[0]
  const path = first?.path.join('.') ?? ''
  return { ok: false, issue: `${path ? `${path}: ` : ''}${first?.message ?? 'invalid event'}` }
}

export type EventMeta = {
  childId: string
  deviceId: string
  /** Defaults to now. Tests pass a fixed clock. */
  ts?: number
  /** Defaults to the device local date of `ts`. CONTRACTS §10: never recomputed later. */
  localDate?: string
  reviewMode?: boolean
  id?: string
}

/** The only place an event is born on the client. uuid v7 keeps ids roughly time ordered. */
export function createEvent<T extends EventType>(
  type: T,
  payload: PayloadOf<T>,
  meta: EventMeta,
): EventOf<T> {
  const ts = meta.ts ?? Date.now()
  const event = {
    id: meta.id ?? uuidv7(),
    childId: meta.childId,
    deviceId: meta.deviceId,
    ts,
    localDate: meta.localDate ?? localDateOf(ts),
    v: 1 as const,
    type,
    payload,
    ...(meta.reviewMode ? { reviewMode: true as const } : {}),
  }
  return event as unknown as EventOf<T>
}

/**
 * Fold order (fold rule 1): synced events first by server `seq`, then unsynced by `ts`, with
 * `id` as the last tie break so two devices fold to the same state.
 */
export function compareEvents(a: AnyEvent, b: AnyEvent): number {
  const sa = a.seq ?? Number.POSITIVE_INFINITY
  const sb = b.seq ?? Number.POSITIVE_INFINITY
  if (sa !== sb) return sa < sb ? -1 : 1
  if (a.ts !== b.ts) return a.ts - b.ts
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** Idempotency key per CONTRACTS §2 rule 3. `null` when the type has no unique key. */
export function idempotencyKey(event: AnyEvent): string | null {
  switch (event.type) {
    case 'brush_session':
      // Rule 3 keys this one "for the verified state", and only that state. A window is
      // allowed more than one session a day: fold rule 2 upgrades none → tried → partial
      // → verified and records anything after a verified as ekstra, and MASTERPLAN §5.1
      // wants that ekstra in the calendar with its shine. Keying every outcome would make
      // the morning's `tried` block the `verified` that follows it.
      return event.payload.window === 'outside' || event.payload.outcome !== 'verified'
        ? null
        : `brush_session:${event.localDate}:${event.payload.window}`
    case 'lesson_completed':
      return `lesson_completed:${event.payload.lessonId}`
    case 'chest_opened':
      return `chest_opened:${event.payload.lessonId}`
    case 'minigame_played':
      return event.payload.tutorial ? `minigame_tutorial:${event.payload.gameId}` : null
    case 'app_opened':
      return `app_opened:${event.localDate}`
    case 'window_closed':
      return `window_closed:${event.payload.localDate}:${event.payload.window}`
    case 'streak_freeze_used':
      return `streak_freeze_used:${event.payload.localDate}`
    case 'recap_completed':
      return `recap_completed:${event.payload.localDate}`
    case 'gorev_answered':
      // Keyed on correct only: a wrong attempt is one row per attempt, like quiz_answered.
      return event.payload.correct
        ? `gorev_answered:${event.payload.localDate}:${event.payload.challengeId}`
        : null
    default:
      return null
  }
}
